import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Checkbox,
  FormControlLabel,
  Chip,
  Alert,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  TextField,
  InputAdornment,
} from "@mui/material";
import useAxios from "../../../../hooks/useAxios";
import "./AttachQuizModal.css";

const AttachQuizModal = ({ open, onClose, quiz }) => {
  // Function to refresh the page
  const refreshPage = () => {
    window.location.reload();
  };

  const [activeStep, setActiveStep] = useState(0);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [assignToAllStudents, setAssignToAllStudents] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [timeLimitMinutes, setTimeLimitMinutes] = useState("");
  const axiosInstance = useAxios();

  const steps = [
    "Select Class",
    "Choose Assignment Type",
    "Select Students",
    "Configure Settings",
    "Confirm Assignment",
  ];

  useEffect(() => {
    if (open) {
      fetchClasses();
      // Reset state when modal opens
      setActiveStep(0);
      setSelectedClass(null);
      setStudents([]);
      setSelectedStudents([]);
      setAssignToAllStudents(false);
      setDueDate("");
      setTimeLimitMinutes("");
      setError(null);
    }
  }, [open, quiz]);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/classes");
      if (response.data.success) {
        setClasses(response.data.classes);
      } else {
        setError(response.data.message || "Failed to fetch classes.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleClassSelect = async (classData) => {
    setSelectedClass(classData);
    setError(null);

    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/quizzes/${quiz.id}/classes/${classData.id}/students`
      );
      if (response.data.success) {
        setStudents(response.data.students);
        setActiveStep(1);
      } else {
        setError(response.data.message || "Failed to fetch students.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to fetch students for this class."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentTypeChange = (event) => {
    const isAssignToAll = event.target.value === "all";
    setAssignToAllStudents(isAssignToAll);
    if (isAssignToAll) {
      setSelectedStudents([]);
      setActiveStep(3); // Skip to confirmation step
    } else {
      setActiveStep(2); // Go to student selection step
    }
  };

  const handleStudentToggle = (studentId) => {
    if (assignToAllStudents) return; // Don't allow individual selection if assigning to all

    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleNext = () => {
    if (activeStep === 1) {
      // Moving from assignment type to student selection
      if (assignToAllStudents) {
        setActiveStep(3); // Skip student selection if assigning to all
      } else {
        setActiveStep(2);
      }
    } else if (activeStep === 2) {
      // Moving from student selection to settings
      if (selectedStudents.length === 0) {
        setError("Please select at least one student.");
        return;
      }
      setActiveStep(3);
    } else if (activeStep === 3) {
      // Moving from settings to confirmation
      setActiveStep(4);
    }
  };

  const handleAttachQuiz = async () => {
    if (!selectedClass) {
      setError("Please select a class.");
      return;
    }

    if (!assignToAllStudents && selectedStudents.length === 0) {
      setError(
        "Please select at least one student or choose to assign to all students."
      );
      return;
    }

    try {
      setAttaching(true);
      setError(null);

      const requestData = {
        assign_to_all_students: assignToAllStudents,
        student_ids: assignToAllStudents ? [] : selectedStudents,
        due_date: dueDate && dueDate.trim() !== "" ? dueDate : null,
        time_limit_minutes: timeLimitMinutes
          ? parseInt(timeLimitMinutes)
          : null,
      };

      await axiosInstance.post(
        `/quizzes/${quiz.id}/classes/${selectedClass.id}/assign-students`,
        requestData
      );

      // Close modal and refresh page
      onClose(true);
      refreshPage();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "An unexpected error occurred while assigning the quiz."
      );
    } finally {
      setAttaching(false);
    }
  };

  const renderClassSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select a Class
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Choose the class you want to assign this quiz to.
      </Typography>

      <Grid container spacing={2}>
        {classes.map((classData) => (
          <Grid item xs={12} sm={6} md={4} key={classData.id}>
            <Card
              className={`class-card ${
                selectedClass?.id === classData.id ? "selected" : ""
              }`}
              onClick={() => handleClassSelect(classData)}
              sx={{
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <CardContent>
                <Typography variant="h6" component="div">
                  {classData.class_name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {classData.description || "No description"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  ID: {classData.unique_identifier}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {classes.length === 0 && (
        <Typography
          variant="body1"
          color="textSecondary"
          textAlign="center"
          py={4}
        >
          No classes available. Create a class first to assign quizzes.
        </Typography>
      )}
    </Box>
  );

  const renderAssignmentType = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Assignment Type
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        How would you like to assign this quiz to students in "
        {selectedClass?.class_name}"?
      </Typography>

      <FormControl component="fieldset">
        <RadioGroup
          value={assignToAllStudents ? "all" : "specific"}
          onChange={handleAssignmentTypeChange}
        >
          <FormControlLabel
            value="all"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1">Assign to all students</Typography>
                <Typography variant="body2" color="textSecondary">
                  All current students in this class will see this quiz, and any
                  new students who join later will also see it.
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="specific"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1">
                  Assign to specific students only
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Only the students you select will see this quiz. New students
                  who join later will not see it.
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>
    </Box>
  );

  const renderStudentSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Students
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Choose which students in "{selectedClass?.class_name}" should have
        access to this quiz.
      </Typography>

      <List>
        {students.map((student) => (
          <ListItem key={student.id} divider>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedStudents.includes(student.id)}
                  onChange={() => handleStudentToggle(student.id)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">{student.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {student.email}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>

      {students.length === 0 && (
        <Typography
          variant="body1"
          color="textSecondary"
          textAlign="center"
          py={4}
        >
          No students found in this class.
        </Typography>
      )}
    </Box>
  );

  const renderConfirmation = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Confirm Assignment
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" gutterBottom>
          <strong>Quiz:</strong> {quiz?.title}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Class:</strong> {selectedClass?.class_name}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Assignment Type:</strong>{" "}
          {assignToAllStudents ? "All students" : "Specific students only"}
        </Typography>

        {!assignToAllStudents && selectedStudents.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Selected Students ({selectedStudents.length}):</strong>
            </Typography>
            <List dense>
              {selectedStudents.map((studentId) => {
                const student = students.find((s) => s.id === studentId);
                return (
                  <ListItem key={studentId}>
                    <ListItemText
                      primary={student?.name}
                      secondary={student?.email}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Quiz Settings
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" gutterBottom>
          <strong>Due Date:</strong>{" "}
          {dueDate && dueDate.trim() !== ""
            ? new Date(dueDate).toLocaleString()
            : "No due date set"}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Time Limit:</strong>{" "}
          {timeLimitMinutes
            ? `${timeLimitMinutes} minutes`
            : "No time limit set"}
        </Typography>
      </Box>
    </Box>
  );

  const renderSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Quiz Settings
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Configure additional settings for this quiz assignment (optional)
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Due Date"
            type="datetime-local"
            value={dueDate || ""}
            onChange={(e) => setDueDate(e.target.value || "")}
            InputLabelProps={{
              shrink: true,
            }}
            helperText="Students won't be able to take the quiz after this date"
            inputProps={{
              placeholder: "Select date and time",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Time Limit"
            type="number"
            value={timeLimitMinutes}
            onChange={(e) => setTimeLimitMinutes(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">minutes</InputAdornment>
              ),
            }}
            helperText="Maximum time students can spend on this quiz"
            inputProps={{
              min: 1,
              max: 1440,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderClassSelection();
      case 1:
        return renderAssignmentType();
      case 2:
        return renderStudentSelection();
      case 3:
        return renderSettings();
      case 4:
        return renderConfirmation();
      default:
        return null;
    }
  };

  if (loading && activeStep === 0) {
    return (
      <Modal
        open={open}
        onClose={() => {
          onClose();
          refreshPage();
        }}
      >
        <Box className="attach-quiz-modal">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
        refreshPage();
      }}
    >
      <Box className="attach-quiz-modal">
        <Box className="modal-header">
          <Typography variant="h5" component="h2">
            Assign Quiz to Students
          </Typography>
          <Button
            onClick={() => {
              onClose();
              refreshPage();
            }}
            color="inherit"
          >
            ✕
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box className="modal-content">
          {quiz && (
            <Box className="quiz-info" mb={3}>
              <Typography variant="h6" gutterBottom>
                {quiz.title}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                ID: {quiz.unique_identifier}
              </Typography>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent()}
        </Box>

        <Box className="modal-footer">
          <Button
            onClick={() => {
              onClose();
              refreshPage();
            }}
            variant="outlined"
            sx={{ mr: 2 }}
          >
            Cancel
          </Button>

          {activeStep > 0 && (
            <Button onClick={handleBack} variant="outlined" sx={{ mr: 2 }}>
              Back
            </Button>
          )}

          {activeStep < 4 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={
                loading ||
                (activeStep === 0 && !selectedClass) ||
                (activeStep === 2 &&
                  !assignToAllStudents &&
                  selectedStudents.length === 0)
              }
            >
              {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Next
            </Button>
          ) : (
            <Button
              onClick={handleAttachQuiz}
              variant="contained"
              disabled={attaching}
            >
              {attaching ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Assigning...
                </>
              ) : (
                "Assign Quiz"
              )}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default AttachQuizModal;
